import { BRAND_NAME } from '@bill-pulse/shared';

/**
 * Balises de référencement par page : titre, description, URL canonique,
 * aperçus de partage (Open Graph) et indexation. Appliquées à chaque navigation
 * et figées dans le HTML pré-généré des pages publiques (scripts/prerender.mjs) :
 * les robots des moteurs IA n'exécutent pas le JavaScript.
 */
export const SITE_URL = 'https://yakafa.fr';

export interface PageSeo {
  title: string;
  description?: string;
  /** Pages privées, formulaires, liens à jeton : jamais indexées. */
  noindex?: boolean;
}

export const DEFAULT_DESCRIPTION =
  'Facturation électronique conforme 2026 (Peppol, Factur-X) pour indépendants et TPE, avec une IA qui rédige vos devis et factures. Essai gratuit.';

function setMeta(attribute: 'name' | 'property', key: string, content: string): void {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(href: string | null): void {
  let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

export function applySeo(seo: PageSeo | undefined, path: string): void {
  const page = seo ?? { title: BRAND_NAME, noindex: true };
  const title = page.title.includes(BRAND_NAME) ? page.title : `${page.title} · ${BRAND_NAME}`;
  const description = page.description ?? DEFAULT_DESCRIPTION;
  const url = `${SITE_URL}${path === '/' ? '/' : path}`;
  document.title = title;
  setMeta('name', 'description', description);
  setMeta('name', 'robots', page.noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
  setMeta('property', 'og:title', title);
  setMeta('property', 'og:description', description);
  setMeta('property', 'og:url', url);
  setMeta('name', 'twitter:title', title);
  setMeta('name', 'twitter:description', description);
  setCanonical(page.noindex ? null : url);
}

/** Données structurées (schema.org) propres à une page ; remplacées à chaque appel. */
export function setJsonLd(id: string, data: object | null): void {
  const selector = `script[type="application/ld+json"][data-id="${id}"]`;
  document.head.querySelector(selector)?.remove();
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.dataset.id = id;
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
}
